import useRenderer from '@hooks/useRenderer'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import { isNotNativeOnlyAd } from '@utils/ads'
import { FunctionComponent } from 'react'
import styled, { css } from 'styled-components'

export interface SectionContentMultilineProps {
  children: SectionContent
}

const StyledSectionContentMultiline = styled.div`
  grid-column: span 6;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  grid-gap: 20px;
  box-sizing: border-box;

  > * {
    grid-column: span 6;
    min-width: 0;
  }

  ${desktopCSS(css`
    padding-left: 170px;
    padding-right: 170px;
  `)}
`

interface LayoutListItemWrapper {
  kind: ['layout', 'item']
  content: [{ kind: string[] }]
}

interface AppNexusMultilineWrapper {
  kind: ['layout', 'multi-line']
  content: {
    kind: ['layout', 'item']
    content: { kind: ['widget', 'ad', 'appnexus'] }[]
  }[]
}

interface PaginationWidget {
  kind: ['widget', 'pagination']
  totalPages: number
  activePage: number
}

type SectionContent = {
  kind: string[]
  content: {
    kind: string[]
    content: SectionContentItem[]
  }[]
}[]

type SectionContentItem =
  | LayoutListItemWrapper
  | AppNexusMultilineWrapper
  | PaginationWidget

type FlattenSectionContent = (content: SectionContent) => {
  paginatedContent: { kind: string[] }[]
  adIndices: number[]
  paginationWidget?: PaginationWidget
}

const StyledAdsWrapper = styled.div`
  grid-column: span 6;
  display: grid;

  ${desktopCSS(css`
    grid-template-columns: repeat(2, minmax(0, 300px));
    align-items: flex-start;
    justify-content: space-between;
    > aside {
      width: 100%;
      &.adm-loaded {
        width: 100%;
      }
    }
  `)}

  ${tabletCSS(css`
    grid-template-columns: repeat(2, minmax(0, 300px));
    align-items: flex-start;
    justify-content: space-between;
    > aside {
      width: 100%;
      &.adm-loaded {
        width: 100%;
      }
    }
  `)}
  
  ${mobileCSS(css`
    grid-template-columns: repeat(1, minmax(0, 1fr));
    grid-row-gap: 20px;
  `)}
`

const cleanUpAppnexusMultiline = (
  appNexusMultilineWrapper: AppNexusMultilineWrapper
) =>
  [
    ...appNexusMultilineWrapper.content[0].content,
    ...appNexusMultilineWrapper.content[1].content,
  ].filter((item) => isNotNativeOnlyAd(item))

const flattenSectionContent: FlattenSectionContent = (content) =>
  content[0].content[0].content.reduce(
    (acc, item, index) => {
      if ((item.kind as string[]).includes('pagination')) {
        acc.paginationWidget = item as PaginationWidget
      } else if ((item.kind as string[]).includes('multi-line')) {
        acc.adIndices.push(index)
        acc.paginatedContent.push(item)
      } else {
        acc.paginatedContent.push((item as LayoutListItemWrapper).content[0])
      }
      return acc
    },
    { paginatedContent: [], adIndices: [] } as ReturnType<FlattenSectionContent>
  )

const SectionContentMultiline: FunctionComponent<
  SectionContentMultilineProps
> = ({ children }) => {
  const Renderer = useRenderer()
  const { paginatedContent, adIndices, paginationWidget } =
    flattenSectionContent(children)

  return (
    <StyledSectionContentMultiline>
      {paginatedContent.map((item, index) => {
        const isAd = adIndices.includes(index)
        if (isAd) {
          return (
            <StyledAdsWrapper key={`ad-wrapper-${index}`}>
              <Renderer>
                {cleanUpAppnexusMultiline(item as AppNexusMultilineWrapper)}
              </Renderer>
            </StyledAdsWrapper>
          )
        } else {
          return <Renderer key={`paginated-element-${index}`}>{item}</Renderer>
        }
      })}
      {!!paginationWidget && <Renderer>{paginationWidget}</Renderer>}
    </StyledSectionContentMultiline>
  )
}

export default SectionContentMultiline
